import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import CustomerCareDTable from '../../../../Shared/CustomerCareDTable';
import HubHistoryDetailModal from './components/HubHistoryDetailModal';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import StatusChip from '../../../../Shared/StatusChip';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { fetchDeviceHistory } from '../../../../../store/devices/actionCreators';
import { formatEventTime, parseDateToLocal } from '../../../../../shared/utils';
import LoadingSpinner from '../../../../Shared/LoadingSpinner';
import {
  Device,
  DeviceHistoryItem,
} from '../../../../../store/devices/deviceTypes';
import NoTabDataCard from '../components/NoTabDataCard';
import { customError, errorBg } from '../../../../../mainTheme';

interface TabComponentProps {
  selectedDevice: Device | null;
}

const HubHistoryTab: React.FC<TabComponentProps> = ({ selectedDevice }) => {
  const dispatch = useDispatch();
  const { deviceHistory, isHistoryLoading } = useSelector(
    (state: RootState) => state.devices,
  );

  const [selectedRow, setSelectedRow] = useState<DeviceHistoryItem | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!selectedDevice) return;
    dispatch(fetchDeviceHistory(selectedDevice?.id));
  }, [dispatch, selectedDevice]);

  const columns = [
    { field: 'issue_id', headerName: 'Issue ID', flex: 2, sortable: true },
    {
      field: 'title',
      headerName: 'Hub event',
      flex: 2,
      renderCell: (params: any) => {
        return (
          <Box display="flex" alignItems="center">
            <StatusChip
              icon={faExclamationTriangle}
              iconColor={customError}
              labelText={params.value}
              conditionalValue={true}
              backgroundColor={errorBg}
            />
          </Box>
        );
      },
    },
    {
      field: 'event_time',
      headerName: 'Event time',
      flex: 2,
      renderCell: (params: any) => formatEventTime(params.value),
    },
    {
      field: 'resolved_time',
      headerName: 'Resolved time',
      flex: 2,
      renderCell: (params: any) =>
        params.value
          ? parseDateToLocal(params.value, 'yyyy-MM-dd HH:mm:ss')
          : 'N/A',
    },
  ];

  const handleRowClick = (row: any) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (isHistoryLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        height: '100%',
      }}
    >
      {deviceHistory && deviceHistory.length ? (
        <CustomerCareDTable
          rows={deviceHistory || []}
          columns={columns}
          pageSize={10}
          defaultSortField="event_time"
          defaultSortDirection="desc"
          hideViewAllButton
          loading={false}
          onRowClick={handleRowClick}
          idField="issue_id"
        />
      ) : (
        <NoTabDataCard />
      )}

      {isModalOpen && selectedRow && (
        <HubHistoryDetailModal
          onClose={handleCloseModal}
          rowData={selectedRow}
          selectedDeviceId={selectedDevice?.id}
        />
      )}
    </Box>
  );
};

export default HubHistoryTab;
